import * as React from 'react';
import { makeStyles, shorthands, Spinner, Title1, tokens } from '@fluentui/react-components';
import { Chat20Filled, Chat20Regular, Person20Filled, Person20Regular } from '@fluentui/react-icons';
import loadable from '@loadable/component';
import { defineMessage, useIntl } from 'react-intl';
import { Redirect } from 'react-router-dom';
import { useExpertDetailQuery } from '@foundershub/api-client';
import { AssumeUserBanner, DemoUserBanner, Header } from '../components';
import { TabNavigation } from '../components/TabNavigation';
import type { NavigationTab } from '../components/TabNavigationTypes';
import { useUserRequired } from '../store';

const FirstTimeExpertModal = loadable(
  () => import(/* webpackChunkName: "first-time" */ '../pages/FirstTimeExpertModal')
);

interface Props {
  children: React.ReactNode;
}

export default function Layout({ children }: Props) {
  const intl = useIntl();
  const [showFirstTimeModal, setShowFirstTimeModal] = React.useState<boolean>(false);
  const { data: user, isLoading, isError } = useExpertDetailQuery();
  const classes = useStyles();
  const baseUser = useUserRequired();

  React.useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('firsttime')) {
      setShowFirstTimeModal(true);
    } else if (!isLoading) {
      setShowFirstTimeModal(!user?.linkedin_profile_url);
    }
  }, [isLoading]);

  if (isError) {
    return <Redirect to="/" />;
  }

  if (isLoading || !user) {
    return <Spinner />;
  }
  const tabs: Array<NavigationTab> = [
    {
      id: '/expert',
      name: intl.formatMessage(profileLabel),
      iconRegular: <Person20Regular />,
      iconFilled: <Person20Filled />,
    },
    {
      id: '/connection-requests',
      name: intl.formatMessage(requestsLabel),
      iconRegular: <Chat20Regular />,
      iconFilled: <Chat20Filled />,
    },
  ];

  return (
    <div className={classes.root}>
      {(showFirstTimeModal || !user.linkedin_profile_url) && <FirstTimeExpertModal />}
      {baseUser.perms.includes('is_assumed_user') ? <AssumeUserBanner /> : null}
      {baseUser.perms.includes('is_demo_user') ? <DemoUserBanner /> : null}
      <Header />
      <div className={classes.main}>
        <div className={classes.navHeader}>
          <div className={classes.title}>
            <Title1 as="h1">{intl.formatMessage(navTitle)}</Title1>
          </div>
          <TabNavigation tabs={tabs} />
        </div>
        <main id="main" className={classes.mainContent}>
          {children}
        </main>
      </div>
    </div>
  );
}

const profileLabel = defineMessage({
  defaultMessage: 'Profile',
  description: 'Expert Profile navigation label in Main Nav drawer',
});
const requestsLabel = defineMessage({
  defaultMessage: 'Requests',
  description: 'Link text to view connection requests',
});

const navTitle = defineMessage({
  defaultMessage: 'Expert Network',
  description: 'title for expert network nav bar',
});

const useStyles = makeStyles({
  root: {
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    alignItems: 'stretch',
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    maxWidth: '100vw',
    flexBasis: 0,
    flexGrow: 999,
  },
  mainContent: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: 0,
    flexGrow: 100,
  },
  navHeader: {
    ...shorthands.padding(tokens.spacingVerticalXL, '72px'),
  },
  title: {
    ...shorthands.margin(tokens.spacingVerticalNone, tokens.spacingHorizontalNone, tokens.spacingVerticalL),
  },
});
